import "rxjs";
import { Epic, ofType } from "redux-observable";
import { RootState } from "../../store";
import * as authActions from "./authActions";
import * as authSelectors from "./authSelectors";
import { ServiceContainer } from "../../serviceContainer";
import { AnyAction } from "redux";
import * as apiResponses from "../../service/api-response";
import { authLoginFailAction } from "./action/authLoginFailAction";
import { AuthFailReason } from "../../model/AuthFailReason";
import { MeSuccessResponse } from "../../service/api-response/MeSuccessResponse";
import * as commonActions from "../common/commonActions";
import i18n from "../../i18n";
import * as organizationActions from "../organization/organizationActions";
import {
  catchError,
  exhaustMap,
  ignoreElements,
  mergeMap,
  switchMap,
  tap,
} from "rxjs/operators";
import { of, Observable } from "rxjs";

const hasNoOrganizations = (meResponse: MeSuccessResponse): boolean => {
  return !meResponse.organizations || meResponse.organizations.length === 0;
};

export const authEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(authActions.AUTH_LOGIN),
    exhaustMap((x: authActions.AuthLoginAction) =>
      services.apiUser.login(x.login, x.password).pipe(
        switchMap((authResponse) => {
          if (apiResponses.isAuthUsernameErrorResponse(authResponse)) {
            return of(authActions.authLoginFailAction(authResponse.errors));
          } else if (apiResponses.isAuthUsernameSuccessResponse(authResponse)) {
            const accessToken = authResponse.data.access_token;
            return services.apiUser.me(accessToken).pipe(
              mergeMap(
                (
                  meResponse
                ): Observable<
                  | authActions.AuthLoginFailAction
                  | authActions.AuthLoginSuccessAction
                > => {
                  if (apiResponses.isMeSuccessResponse(meResponse)) {
                    if (hasNoOrganizations(meResponse)) {
                      services.toastr.error(
                        "Login error",
                        `You don't have access to any organization.
                         Please contact us, if you think that's an error.
                        `
                      );
                      return of(
                        authActions.authLoginFailAction([
                          { code: AuthFailReason.NoOrganizations },
                        ])
                      );
                    }

                    return of(
                      authActions.authLoginSuccessAction(
                        accessToken,
                        meResponse.organizations,
                        meResponse.me,
                        authResponse.data.roles
                      )
                    );
                  }

                  return of(
                    authActions.authLoginFailAction([
                      { code: AuthFailReason.Other },
                    ])
                  );
                }
              ),
              tap(() =>
                services.authStorage.saveAuthData({
                  token: accessToken,
                  roles: authResponse.data.roles,
                })
              )
            );
          } else {
            throw new Error("Unknown response");
          }
        }),
        catchError((e) => {
          if (e.response && (e.status === 400 || e.status === 401)) {
            return of(
              authLoginFailAction([
                { code: AuthFailReason.UnknownUserOrWrongPassword },
              ])
            );
          }

          services.toastr.error("Error", e.message);
          return of(authLoginFailAction());
        })
      )
    )
  );

export const selectOrganizationEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    ofType(authActions.SELECT_ORGANIZATION),
    switchMap((action: authActions.SelectOrganizationAction) => {
      const state = state$.value;
      const organization = authSelectors.organizationByKeyName(
        state,
        action.organizationKeyName
      );
      if (!organization) {
        services.toastr.error(
          "Select organization fail",
          `You don't have permission to access "${action.organizationKeyName}" organization.
           Please contact us, if you think that's an error.
          `
        );
        return of(
          authActions.selectOrganizationFailAction(action.organizationKeyName)
        );
      }

      if (organization.configIsVenueADeal) {
        i18n.setDefaultNamespace("venue_is_a_deal");
      }

      const tokenData = services.authStorage.getToken();

      // ignore selecting the same organization
      const selectedOrganization = authSelectors.selectedOrganization(
        state$.value
      );
      if (
        selectedOrganization &&
        selectedOrganization.keyName === action.organizationKeyName
      ) {
        return of(
          authActions.selectOrganizationSuccessAction(selectedOrganization)
        );
      }

      return services.apiOrganization
        .getUISettings(tokenData.token, organization.keyName)
        .pipe(
          mergeMap((uiSettings) => {
            return of<AnyAction>(
              organizationActions.getUISettingsSuccess(
                organization.keyName,
                uiSettings
              ),
              authActions.selectOrganizationSuccessAction(organization)
            );
          }),
          catchError(() => {
            services.toastr.error(
              "Select organization fail",
              `There was problem fetching organization settings`
            );

            return of<AnyAction>(
              authActions.logoutAction(),
              organizationActions.getUISettingsFail(organization.keyName),
              authActions.selectOrganizationFailAction(
                action.organizationKeyName
              )
            );
          })
        );
    })
  );

export const loginOnAppInitEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(commonActions.APP_INIT_ACTION),
    mergeMap(() => {
      if (!services.authStorage.hasToken()) {
        return of(commonActions.appInitSuccessAction());
      }

      const tokenData = services.authStorage.getToken();

      return services.apiUser.me(tokenData.token).pipe(
        mergeMap((meResponse) => {
          if (!apiResponses.isMeSuccessResponse(meResponse)) {
            services.authStorage.removeToken();
            return of(commonActions.appInitSuccessAction());
          } else {
            if (hasNoOrganizations(meResponse)) {
              services.authStorage.removeToken();
              return of(commonActions.appInitSuccessAction());
            }

            return of<any>(
              authActions.authAutologinSuccessAction(
                tokenData.token,
                meResponse.organizations,
                meResponse.me,
                tokenData.roles
              ),
              commonActions.appInitSuccessAction()
            );
          }
        }),
        catchError(() => {
          services.authStorage.removeToken();

          return of(commonActions.appInitSuccessAction());
        })
      );
    })
  );

export const logoutEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(authActions.AUTH_LOGOUT),
    tap(() => services.authStorage.removeToken()),
    ignoreElements()
  );
